<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          <span>Edit Weekly Room Reward</span>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="4">
              <v-text-field
                v-model="form.weekly_room_target"
                outlined
                label="Weekly Room Target *"
                prepend-inner-icon="mdi-target"
                type="number"
                min="1"
                :error-messages="errors.weekly_room_target"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.weekly_reward"
                outlined
                label="Weekly Reward *"
                prepend-inner-icon="mdi-bitcoin"
                type="number"
                min="1"
                :error-messages="errors.weekly_reward"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.frame"
                outlined
                label="Frame"
                prepend-inner-icon="mdi-image-frame"
                :error-messages="errors.frame"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.medal"
                outlined
                label="Medal"
                prepend-inner-icon="mdi-medal"
                :error-messages="errors.medal"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-switch
                v-model="form.is_active"
                label="Is Active"
                color="primary"
                inset
              ></v-switch>
            </v-col>
          </v-row>

          <!-- Preview Section -->
          <v-divider class="mt-5 mb-5"></v-divider>
          <v-row v-if="form.weekly_room_target">
            <v-col sm="12">
              <h4 class="mb-3">Reward Preview</h4>
              <v-card outlined>
                <v-card-text>
                  <v-row>
                    <v-col sm="3">
                      <strong>Target:</strong> {{ formatNumber(form.weekly_room_target) }} rooms
                    </v-col>
                    <v-col sm="3">
                      <strong>Reward:</strong> {{ formatNumber(form.weekly_reward) }} coins
                    </v-col>
                    <v-col sm="3">
                      <strong>Tier:</strong>
                      <v-chip
                        small
                        :color="getTierColor(form.weekly_room_target)"
                        text-color="white"
                      >
                        {{ getRewardTier(form.weekly_room_target) }}
                      </v-chip>
                    </v-col>
                    <v-col sm="3">
                      <strong>Status:</strong>
                      <v-chip
                        small
                        :color="form.is_active ? 'teal' : 'red'"
                        text-color="white"
                      >
                        {{ form.is_active ? 'Active' : 'Inactive' }}
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-divider class="mt-5 mb-5"></v-divider>

          <v-btn
            :loading="isLoading"
            :disabled="isLoading"
            rounded
            color="primary"
            dark
            @click="save"
            class="mr-3"
          >
            Update Weekly Room Reward
          </v-btn>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "WeeklyRoomRewardEditPage",
  data() {
    return {
      form: {
        is_active: true,
        weekly_room_target: null,
        weekly_reward: null,
        frame: "",
        medal: "",
      },
      isLoading: false,
      isDeleting: false,
    };
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      let encryptedId = this.$route.params.weeklyRoomRewardId;

      try {
        let response = await axios.get(`/weekly_room_rewards/${encryptedId}`);
        this.form = response.data.data;
      } catch (error) {
        console.error("Error fetching weekly room reward:", error);
        this.$store.dispatch("snackBar", {
          color: "error",
          message: "Failed to load weekly room reward",
        });
        this.$router.push("/weekly_room_rewards");
      } finally {
        this.isLoading = false;
      }
    },

    async save() {
      this.isLoading = true;
      this.errors = {};

      try {
        let response = await axios.patch(
          `/weekly_room_rewards/${this.$route.params.weeklyRoomRewardId}`,
          this.form
        );

        if (response.data.success) {
          this.$store.dispatch("snackBar", {
            color: "success",
            message: "Weekly Room Reward updated successfully",
          });
          this.$router.push("/weekly_room_rewards");
        } else {
          this.$store.dispatch("snackBar", {
            color: "error",
            message: response.data.message || "Failed to update weekly room reward",
          });
        }
      } catch (error) {
        console.error("Error updating weekly room reward:", error);

        if (error.response && error.response.status === 422) {
          this.errors = error.response.data.errors || {};
        }

        this.$store.dispatch("snackBar", {
          color: "error",
          message: error.response?.data?.message || "Failed to update weekly room reward",
        });
      } finally {
        this.isLoading = false;
      }
    },

    async deleteReward() {
      if (!confirm("Are you sure you want to delete this weekly room reward?")) {
        return;
      }

      this.isDeleting = true;

      try {
        let response = await axios.delete(
          `/weekly_room_rewards/${this.$route.params.weeklyRoomRewardId}`
        );

        if (response.data.success) {
          this.$store.dispatch("snackBar", {
            color: "success",
            message: "Weekly Room Reward deleted successfully",
          });
          this.$router.push("/weekly_room_rewards");
        } else {
          this.$store.dispatch("snackBar", {
            color: "error",
            message: response.data.message || "Failed to delete weekly room reward",
          });
        }
      } catch (error) {
        console.error("Error deleting weekly room reward:", error);
        this.$store.dispatch("snackBar", {
          color: "error",
          message: error.response?.data?.message || "Failed to delete weekly room reward",
        });
      } finally {
        this.isDeleting = false;
      }
    },

    formatNumber(value) {
      if (!value) return "0";
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    getRewardTier(target) {
      const targetValue = parseInt(target) || 0;

      if (targetValue >= 2000) return 'Legendary';
      if (targetValue >= 1500) return 'Epic';
      if (targetValue >= 1000) return 'Diamond';
      if (targetValue >= 500) return 'Platinum';
      if (targetValue >= 200) return 'Gold';
      if (targetValue >= 100) return 'Silver';
      if (targetValue >= 50) return 'Bronze';
      return 'Starter';
    },

    getTierColor(target) {
      const tier = this.getRewardTier(target);
      const colorMap = {
        'Legendary': 'purple',
        'Epic': 'deep-purple',
        'Diamond': 'blue',
        'Platinum': 'cyan',
        'Gold': 'amber',
        'Silver': 'grey',
        'Bronze': 'brown',
        'Starter': 'green'
      };
      return colorMap[tier] || 'grey';
    },
  },
};
</script>

<style scoped>
.v-text-field >>> .v-input__control .v-input__slot {
  min-height: 56px;
}
</style>